import { useEffect } from 'react';

type PhotoLightboxProps = {
  src: string | null;
  onClose: () => void;
};

export function PhotoLightbox({ src, onClose }: PhotoLightboxProps) {
  useEffect(() => {
    if (!src) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [src, onClose]);

  if (!src) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Photo">
      <button type="button" className="absolute inset-0 bg-slate-900/75" aria-label="Dismiss photo" onClick={onClose} />
      <div className="relative z-10 max-h-[90vh] max-w-[90vw]">
        <img src={src} alt="" className="max-h-[90vh] max-w-[90vw] rounded-xl object-contain shadow-2xl" />
        <button
          type="button"
          onClick={onClose}
          className="absolute -right-2 -top-2 rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-slate-800 shadow-md min-h-[38px] flex items-center justify-center hover:bg-slate-50 transition-colors focus:outline-none focus:ring-2 focus:ring-brand/30"
          aria-label="Close photo"
        >
          Close
        </button>
      </div>
    </div>
  );
}

type PhotoThumbProps = {
  src: string;
  onClick: () => void;
  sizeClass?: string;
  roundedClass?: string;
  label?: string;
};

export function PhotoThumb({
  src,
  onClick,
  sizeClass = 'h-10 w-10',
  roundedClass = 'rounded-full',
  label = 'View photo',
}: PhotoThumbProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={`${sizeClass} ${roundedClass} shrink-0 overflow-hidden ring-2 ring-slate-200 hover:ring-brand focus:outline-none focus:ring-2 focus:ring-brand/50 transition-all flex items-center justify-center`}
    >
      <img src={src} alt="" className="h-full w-full object-cover" />
    </button>
  );
}
